import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { StateStorage } from 'zustand/middleware';
import User from '@/types/userType';
import { useAuthStore } from './authState';
import useOrganizationStore from './orgState';
import useUserContextStore from '@/contexts/UserContext';
import useUserStore from './userState';

interface AccountMismatchState {
  existingUser: User | null;
  newEmail: string | null;
  provider: string | null;
  username: string | null;
  returnUrl: string | null;
  hasMismatch: boolean;
  setMismatch: (data: {
    existingUser: User;
    newEmail: string;
    provider: string;
    username: string;
    returnUrl?: string | null;
  }) => void;
  clear: () => void;
}

const INITIAL: Omit<AccountMismatchState, 'setMismatch' | 'clear'> = {
  existingUser: null,
  newEmail: null,
  provider: null,
  username: null,
  returnUrl: null,
  hasMismatch: false,
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

const sessionStorageOrNoop = (): StateStorage =>
  typeof window !== 'undefined' ? window.sessionStorage : noopStorage;

export const useAccountMismatchStore = create<AccountMismatchState>()(
  persist(
    (set) => ({
      ...INITIAL,
      setMismatch: ({
        existingUser,
        newEmail,
        provider,
        username,
        returnUrl,
      }) =>
        set({
          existingUser,
          newEmail,
          provider,
          username,
          returnUrl: returnUrl ?? null,
          hasMismatch: true,
        }),
      clear: () => {
        set(INITIAL);
        useAuthStore.getState().clearAllAuth();
        useOrganizationStore.getState().clearOrganization();
        useUserContextStore.getState().reset();
        useUserStore.getState().clearUser();
      },
    }),
    {
      name: 'account-mismatch-storage',
      storage: createJSONStorage(sessionStorageOrNoop),
    }
  )
);
